import { AfterViewInit, Component, ElementRef, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Route, Router } from '@angular/router';
import { LoaderService } from '../../Services/loader-service.service';
import { ApiCallService } from '../../Services/api-call-service.service';
import { UtilsService } from '../../Services/utils.service';
import { ErrorhandlingService } from '../../Services/error-handling.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  standalone: true,
  selector: 'app-profile',
  imports: [CommonModule],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements AfterViewInit, OnDestroy {
  //? Reference to the modal root so we can teleport it to <body>
  @ViewChild('lvlmBackdrop') lvlmBackdrop?: ElementRef<HTMLElement>;

  //? Reference to the Add Funds modal root so we can teleport it to <body>
  @ViewChild('addFundsBackdrop') addFundsBackdrop?: ElementRef<HTMLElement>;

  //? Reference to the Withdraw modal root so we can teleport it to <body>
  @ViewChild('withdrawBackdrop') withdrawBackdrop?: ElementRef<HTMLElement>;

  constructor(
    private router: Router,
    private loaderService: LoaderService,
    private apiCallService: ApiCallService,
    private handleerror: ErrorhandlingService,
    private toastr: ToastrService,
    private utilsService: UtilsService,
    private renderer: Renderer2
  ) { }

  //? Move the modal out of the component subtree onto <body> so no
  //? transformed / clipped ancestor can affect its fixed positioning.
  ngAfterViewInit() {
    const el = this.lvlmBackdrop?.nativeElement;
    if (el) {
      this.renderer.appendChild(document.body, el);
    }
    const af = this.addFundsBackdrop?.nativeElement;
    if (af) {
      this.renderer.appendChild(document.body, af);
    }
    const wd = this.withdrawBackdrop?.nativeElement;
    if (wd) {
      this.renderer.appendChild(document.body, wd);
    }
  }

  ngOnDestroy() {
    //? Clean up the teleported nodes and restore body scroll
    const el = this.lvlmBackdrop?.nativeElement;
    if (el && el.parentNode) {
      el.parentNode.removeChild(el);
    }
    const af = this.addFundsBackdrop?.nativeElement;
    if (af && af.parentNode) {
      af.parentNode.removeChild(af);
    }
    const wd = this.withdrawBackdrop?.nativeElement;
    if (wd && wd.parentNode) {
      wd.parentNode.removeChild(wd);
    }
    this.renderer.removeClass(document.body, 'lvlm-open');
  }

  ngOnInit() {
    //? User Data from Local Storage
    this.email = localStorage.getItem('email') ?? '';
    this.name = localStorage.getItem('userName') ?? '';
    this.username = localStorage.getItem('userName') ?? '';

    //? Profile Image Dynamic Update
    this.utilsService.profileImage$.subscribe((image) => {
      this.profileImage = image;
    });
  }
  // * User Profile Data
  email: string = '';
  name: string = '';
  username: string = '';
  profileImage: string = '';

  //* Default assets (placed in public/Images/profile/)
  defaultAvatar: string = 'Images/profile/default-male.jpg';

  //* Placeholder data for the visual shell (wire to real APIs later)
  country: string = 'United States';
  rankName: string = 'Bronze';
  level: number = 0;
  xp: number = 0;
  xpPercent: number = 0;
  nextRank: string = 'Silver';
  xpToNext: number = 2000;
  scoreRedeemLimit: number = 100;
  withdrawLimit: number = 100;
  mainWallet: number = 0;
  bonusWallet: number = 0;

  //* ---- Level Journey modal ----
  showLevelModal: boolean = false;

  openLevelModal() {
    this.showLevelModal = true;
    this.renderer.addClass(document.body, 'lvlm-open');
  }

  closeLevelModal() {
    this.showLevelModal = false;
    this.renderer.removeClass(document.body, 'lvlm-open');
  }

  //* Dummy ladder data for the Level Journey popup
  levels: Array<{
    name: string;
    level: number;
    state: 'current' | 'unlocked' | 'locked';
    xp?: number;
    unlockXp?: number;
    scoreLimit: string;
    withdraw: string;
    img?: string;
    icon: string;
  }> = [
    { name: 'Bronze', level: 0, state: 'current', xp: 0, scoreLimit: '100', withdraw: '100', img: 'Images/profile/bronze.png', icon: '🥉' },
    { name: 'Silver', level: 1, state: 'locked', unlockXp: 2000, scoreLimit: '200', withdraw: '200', img: 'Images/profile/silver.webp', icon: '🥈' },
    { name: 'Gold', level: 2, state: 'locked', unlockXp: 10000, scoreLimit: '500', withdraw: '500', img: 'Images/profile/golden.png', icon: '🥇' },
    { name: 'Crown', level: 3, state: 'locked', unlockXp: 30000, scoreLimit: '800', withdraw: '800', img: 'Images/profile/crown1.webp', icon: '👑' },
    { name: 'Diamond', level: 4, state: 'locked', unlockXp: 66000, scoreLimit: '5,000', withdraw: '5,000', img: 'Images/profile/diamond-icon.webp', icon: '💎' },
    { name: 'Ace', level: 5, state: 'locked', unlockXp: 122000, scoreLimit: '10,000', withdraw: '10,000', img: 'Images/profile/level_image_8bb88b7f076e472cb56aad618cbd21fa.webp', icon: '🃏' },
  ];

  //* Open the shared global deposit popup (same one the header's DEPOSIT
  //* button opens). Driven by <app-add-wallet> in app.component via the
  //* error-handling service's showModalSubject.
  openDepositModal() {
    this.handleerror.showModalSubject.next(true);
  }

  //* ---- Add Funds modal ----
  showAddFunds: boolean = false;

  openAddFunds() {
    this.showAddFunds = true;
    this.renderer.addClass(document.body, 'lvlm-open');
  }

  closeAddFunds() {
    this.showAddFunds = false;
    this.renderer.removeClass(document.body, 'lvlm-open');
  }

  //* Dummy payment methods for the Add Funds popup (visual shell — wire to real APIs later).
  //* `icon` renders an inline SVG glyph; `img` renders a brand logo. `tone` picks the status pill color.
  fundSections: Array<{
    label: string;
    lead: 'bolt' | 'bitcoin' | null;
    methods: Array<{
      title: string;
      subtitle: string;
      bonus: number;
      status: string;
      tone: 'green' | 'gold';
      icon?: 'card-gold' | 'apple' | 'card' | 'bitcoin';
      img?: string;
    }>;
  }> = [
    {
      label: 'Instant',
      lead: 'bolt',
      methods: [
        { title: 'Card', subtitle: 'Visa · Mastercard', bonus: 60, status: 'INSTANT', tone: 'green', icon: 'card-gold' },
        { title: 'CashApp', subtitle: 'Pay via CashApp link', bonus: 50, status: 'INSTANT', tone: 'green', img: 'https://cmax-2.pages.dev/assets/payments/cashapp.png' },
        { title: 'Google Pay', subtitle: 'Pay with Google Pay', bonus: 50, status: 'INSTANT', tone: 'green', img: 'https://cmax-2.pages.dev/assets/payments/google-pay.webp' },
        { title: 'Apple Pay', subtitle: 'Pay with Apple Pay', bonus: 50, status: 'INSTANT', tone: 'green', icon: 'apple' },
        { title: 'PayPal (Instant)', subtitle: 'Pay instantly with PayPal', bonus: 50, status: 'INSTANT', tone: 'green', img: 'https://cmax-2.pages.dev/assets/payments/paypal.svg' },
        { title: 'Debit / Credit Card', subtitle: 'Pay by card via PayPal', bonus: 50, status: 'INSTANT', tone: 'green', icon: 'card' },
      ],
    },
    {
      label: 'Crypto',
      lead: 'bitcoin',
      methods: [
        { title: 'Crypto', subtitle: 'BTC · ETH · USDT via NowPayments', bonus: 90, status: 'AUTO', tone: 'gold', icon: 'bitcoin' },
      ],
    },
    {
      label: 'Manual · Approved Within Minutes',
      lead: null,
      methods: [
        { title: 'Chime', subtitle: 'Send to our Chime tag', bonus: 70, status: 'MANUAL', tone: 'gold', img: 'https://cmax-2.pages.dev/assets/payments/chime.png' },
      ],
    },
  ];

  //* ---- Withdraw modal ----
  showWithdraw: boolean = false;

  openWithdraw() {
    this.showWithdraw = true;
    this.renderer.addClass(document.body, 'lvlm-open');
  }

  closeWithdraw() {
    this.showWithdraw = false;
    this.renderer.removeClass(document.body, 'lvlm-open');
  }

  //* Dummy withdrawal methods for the Withdraw popup (visual shell)
  withdrawMethods: Array<{ title: string; sub: string; img: string }> = [
    { title: 'PayPal', sub: 'Withdraw to your PayPal', img: 'https://cmax-2.pages.dev/assets/payments/paypal.svg' },
    { title: 'Chime', sub: 'Withdraw to your Chime', img: 'https://cmax-2.pages.dev/assets/payments/chime.png' },
  ];

  //* Bulleted info lines shown at the bottom of the Withdraw popup
  withdrawInfo: Array<{ pre: string; strong?: string }> = [
    { pre: 'Minimum withdrawal: ', strong: '$30.00' },
    { pre: 'Processed within ', strong: '2 hours' },
    { pre: 'Withdrawals are final once processed' },
  ];

  //* ---- My Game Accounts ----
  //* Dummy data for now — sourced from the Dashboard games (games-landing quickGames).
  //* Replace `username` with real linked-account handles when the API is wired.
  gameAccounts: Array<{ name: string; username: string; image: string; route: string }> = [
    { name: 'Aviator', username: '@aviator_8821', image: 'Images/Avaitar.png', route: '/dashboard/Avaitar' },
    { name: 'Baccarat', username: '@vip_baccarat', image: 'Images/Baccarat.png', route: '/dashboard/Baccaret' },
    { name: 'Treasure', username: '@treasure_hunt', image: 'Images/treasure-vault.png', route: '/dashboard/TreasurePick' },
    { name: 'Mines', username: '@miner_4471', image: 'bomb.png', route: '/dashboard/Mines' },
    { name: 'Plinko', username: '@plinko_pro', image: 'plinko.png', route: '/dashboard/Plinko' },
    { name: 'Roulette', username: '@roulette_king', image: 'Rouleet.png', route: '/dashboard/Roulette' },
    { name: 'Keno', username: '@keno_player', image: 'Images/keno.png', route: '/dashboard/Keno' },
    { name: 'Scratch', username: '@lucky_scratch', image: 'Images/sactrach.png', route: '/dashboard/SectrechCards' },
  ];

  //* Launch a game account
  playGame(route: string) {
    this.router.navigate([route]);
  }

  //* Tracks which Account Info field was just copied (drives the checkmark animation)
  copiedField: string | null = null;
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;

  //* Copy a field value to the clipboard
  copyToClipboard(value: string, label: string = 'Copied') {
    if (!value) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        // Swap the button icon to a checkmark, then revert after a short delay
        this.copiedField = label;
        if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
        this.copyResetTimer = setTimeout(() => {
          this.copiedField = null;
          this.copyResetTimer = null;
        }, 1800);
      })
      .catch(() => this.toastr.error('Unable to copy', 'Error'));
  }

  //* Router navigation function
  changeRoute(path: string) {
    this.router.navigate([path]);
  }

  //* Open the existing KYC Verification form (same route the header/add-wallet use)
  openKycForm() {
    this.router.navigate(['dashboard/KYCform']);
  }

  //* Open the existing Change Password page
  openChangePassword() {
    this.router.navigate(['dashboard/changePass']);
  }

  // * File Upload Function
  saveUserProfileImage() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';

    fileInput.onchange = async (event: Event) => {
      const file = (event.target as HTMLInputElement)?.files?.[0];

      if (file) {
        try {
          //? Show loader while processing
          this.loaderService.show();

          //? Convert the file to a base64 string
          const base64Image = await this.convertFileToBase64(file);

          //? Create the API payload
          const payload: any = {
            base64Image: base64Image,
          };

          //? Make the API call
          this.apiCallService
            .PostCallWithToken(payload, 'User/SaveUserProfileImage')
            .subscribe({
              next: (response) => {
                if (response && response.responseCode === 200) {
                  this.profileImage = base64Image;

                  //? Set Profile Image for Dynamic Update
                  this.utilsService.setProfileImage(base64Image);
                  this.loaderService.triggerFunction();
                  // console.log(
                  //   'Profile image saved successfully:',
                  //   response.responseMessage
                  // );
                } else {
                  // console.error(
                  //   'Failed to save profile image:',
                  //   response.responseMessage
                  // );
                  this.handleerror.handleResponseError(response);
                }
                // this.loaderService.hide();
              },
              error: (error) => {
                // console.error('Error saving profile image:', error);
                // this.loaderService.hide();
                this.handleerror.handleHttpError(error);
              },
              complete: () => {
                this.loaderService.hide();
                // console.log('Profile image save process completed.');
              },
            });
        } catch (error) {
          // console.error('Error processing image file:', error);
          this.loaderService.hide();
          // this.handleerror.handleHttpError(error);
        }
      }
    };
    fileInput.click();
  }

  // Delete User Profile Image 
  deleteUserProfileImage() {

    this.loaderService.show();

    this.apiCallService.PostCallWithToken({}, 'User/DeleteUserProfileImage').subscribe({
      next: (response) => {
        if (response && response.responseCode === 200) {
          this.profileImage = "";
          this.utilsService.setProfileImage("");
          this.loaderService.triggerFunction();
          this.toastr.success(response.responseMessage, 'Success');
        } else {
          this.handleerror.handleResponseError(response);
        }
      },
      error: (error) => {
        this.handleerror.handleHttpError(error);
      },
      complete: () => {
        this.loaderService.hide();
      },
    });
  }


  private convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }
}
